import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { VehicleType } from '@polaris-customerweb/sharedcomponents.core';
import { GeoBounds } from '@polaris-customerweb/sharedcomponents.shared';
import {
    ShowOnMapService,
    Waypoint,
} from '@polaris-customerweb/sharedcomponents.user-content';
import { find, isUndefined } from 'lodash-es';
import type { Point } from 'mapbox-gl';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import {
    DeleteWaypoint,
    UpdatePublicWaypoint,
    UpdateWaypoint,
} from 'src/app/components/waypoint/waypoint.actions';
import {
    selectPublicWaypointsInView,
    selectWaypoints,
} from 'src/app/components/waypoint/waypoint.reducer';
import { AppState } from 'src/app/models/app-state';
import { GeoJsonFeatureCollection } from 'src/app/models/geojson-feature-collection';
import { Tag } from '../models/tag';
import { UserAuthService } from '../state/user-auth/user-auth.service';
import { TagService } from './tag.service';
import { WaypointPhotoResource } from './waypoint-photo-resource.service';
import { WaypointResourceService } from './waypoint-resource.service';

@Injectable({ providedIn: 'root' })
export class WaypointService {
    waypoints: Waypoint[] = [];
    publicWaypoints: Waypoint[];

    private isAuthenticated: boolean;
    private defaultIcon = 'mark_solid';

    constructor(
        private store: Store<AppState>,
        private showOnMapService: ShowOnMapService,
        userAuthService: UserAuthService,
        private waypointPhotoResource: WaypointPhotoResource,
        private waypointResource: WaypointResourceService,
        private tagService: TagService
    ) {
        store
            .pipe(select(selectWaypoints))
            .subscribe((waypoints) => (this.waypoints = waypoints));

        store
            .pipe(select(selectPublicWaypointsInView))
            .subscribe(
                (publicWaypoints) => (this.publicWaypoints = publicWaypoints)
            );

        userAuthService.isAuthenticated$.subscribe(
            (isAuthenticated) => (this.isAuthenticated = isAuthenticated)
        );
    }

    //#region Get
    getWaypoints$(): Observable<Waypoint[]> {
        if (!this.isAuthenticated) {
            return of([]);
        }

        return this.waypointResource.getWaypointsForUser();
    }

    getPublicWaypoints(
        boundingBox: GeoBounds,
        vehicleType?: VehicleType
    ): Observable<Waypoint[]> {
        return this.waypointResource.getPublicWaypoints(
            boundingBox,
            vehicleType
        );
    }

    getWaypoint(id: string): Observable<Waypoint> {
        const waypoint = this.getWaypointFromCache(id);
        if (waypoint) {
            return of(waypoint);
        }

        return this.waypointResource.getWaypointById(id).pipe(
            tap((waypointFromServer) => {
                if (waypointFromServer.isPublic) {
                    this.store.dispatch(
                        new UpdatePublicWaypoint(waypointFromServer)
                    );
                } else {
                    this.store.dispatch(new UpdateWaypoint(waypointFromServer));
                }
            })
        );
    }

    getPublicWaypoint(id: string): Observable<Waypoint> {
        const waypoint = this.getWaypointFromCache(id);
        if (waypoint) {
            return of(waypoint);
        }

        return this.waypointResource.getPublicWaypointById(id).pipe(
            tap((waypointFromServer) => {
                if (waypointFromServer.isPublic) {
                    this.store.dispatch(
                        new UpdatePublicWaypoint(waypointFromServer)
                    );
                }
            })
        );
    }

    getWaypointFromCache(id: string): Waypoint | undefined {
        return find(this.waypoints.concat(this.publicWaypoints), {
            id,
        });
    }

    //#endregion

    //#region Create
    createWaypoint(
        waypoint: Waypoint,
        shouldDeletePhoto: boolean,
        fileToUpload?: File
    ): Observable<Waypoint> {
        return this.waypointResource.createWaypoint(waypoint).pipe(
            switchMap((changedWaypoint) =>
                this.updatePhotos(
                    changedWaypoint,
                    shouldDeletePhoto,
                    fileToUpload
                )
            ),
            tap((changedWaypoint) => {
                this.showOnMap(changedWaypoint);
                this.store.dispatch(new UpdateWaypoint(changedWaypoint));
            })
        );
    }

    //#endregion

    //#region Update
    updateWaypoint(
        waypoint: Waypoint,
        shouldDeletePhoto: boolean,
        fileToUpload?: File
    ): Observable<Waypoint> {
        waypoint.updateModifiedDate();
        return this.waypointResource.updateWaypoint(waypoint).pipe(
            switchMap((changedWaypoint) =>
                this.updatePhotos(
                    changedWaypoint,
                    shouldDeletePhoto,
                    fileToUpload
                )
            ),
            tap((changedWaypoint) => {
                this.store.dispatch(new UpdateWaypoint(changedWaypoint));
            })
        );
    }

    //#endregion

    //#region Delete
    deleteWaypoint(waypointId: string): Observable<void> {
        return this.waypointResource.deleteWaypoint(waypointId).pipe(
            tap(() => {
                this.store.dispatch(new DeleteWaypoint(waypointId));
            })
        );
    }

    deleteSelectedWaypoints(
        waypoints: Waypoint[]
    ): Observable<{ id: string; success: boolean }[]> {
        if (waypoints.length === 0) {
            return of([]);
        }

        const resultObservables = waypoints.map((waypoint: Waypoint) => {
            return this.deleteWaypoint(waypoint.id).pipe(
                catchError(() => {
                    return of('failed');
                }),
                map((result) => {
                    return { id: waypoint.id, success: result !== 'failed' };
                })
            );
        });

        return forkJoin(resultObservables);
    }

    //#endregion

    waypointsToGeoJson(waypoints: Waypoint[] = []): GeoJsonFeatureCollection {
        const features = waypoints.map((waypoint) => waypoint.toGeoJson());
        return new GeoJsonFeatureCollection(features);
    }

    offSetLatForWaypointEditImage(
        boundingBox: GeoBounds,
        lat: number,
        point: Point
    ): number {
        if (isUndefined(point)) {
            return lat;
        }

        const boundingBoxLat = boundingBox.north;
        const latDifference = boundingBoxLat - lat;
        const perPixelLat = latDifference / point.y || 1;
        const missingLat = perPixelLat * 15; // 15 is the height of the image for the waypoint drop pin

        return lat - missingLat;
    }

    removeFromMap(waypoint: Waypoint): void {
        this.showOnMapService.setShouldShow(waypoint.id, false);
        this.store.dispatch(new UpdateWaypoint(waypoint));
    }

    showOnMap(waypoint: Waypoint): void {
        this.showOnMapService.setShouldShow(waypoint.id, true);
        this.store.dispatch(new UpdateWaypoint(waypoint));
    }

    getWaypointTag$(waypoint: Waypoint): Observable<Tag | undefined> {
        const tag = waypoint.tags[0] || this.defaultIcon;

        return this.tagService
            .getTags('userwaypoints-initial')
            .pipe(map((tags) => find(tags, (t) => t.tagName === tag)));
    }

    getWaypointTag(waypoint: Waypoint, tags: Tag[]): Tag | undefined {
        const tag = waypoint.tags[0] || this.defaultIcon;

        return tags.find((t) => t.tagName === tag);
    }

    private updatePhotos(
        waypoint: Waypoint,
        shouldDeletePhoto: boolean,
        fileToUpload: File | undefined
    ): Observable<Waypoint> {
        if (!shouldDeletePhoto) {
            return of(waypoint);
        }

        return this.waypointPhotoResource.deletePhoto(waypoint.id).pipe(
            switchMap(() => {
                if (fileToUpload) {
                    return this.waypointPhotoResource.uploadPhoto(
                        waypoint.id,
                        fileToUpload
                    );
                } else {
                    return of(waypoint);
                }
            })
        );
    }
}
